import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import template from './ns-add-to-bag.html';

import {events, StringService, StoreFrontSponsorStorageService, util} from '@nuskin/ns-util';
import {CartService} from '@nuskin/ns-shop';

/**
 * <p>The ns-add-to-bag component displays a popup when a product is added to the cart.
 * This component subscribes to the ADD_TO_CART event found under events.shop.ADD_TO_CART.</p>
 *
 * <h2>Demo</h3>
 *
 * <demo-snippet class="demoSnippet">
 *  <template>
 *    <ns-add-to-bag added-to-cart="true" is-demo-mode="true"></ns-add-to-bag>
 *  </template>
 * </demo-snippet>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Styling
 *
 *      ##### `<add-to-bag>` currently has no properties or mixins for styling.
 *
 *      Custom property                 | Description                                           | Default
 *      --------------------------------|-------------------------------------------------------|-------------------
 *      `--tbd`                          | We should probably have a mixin for stuff            | `{}`
 *
 *      ### Slots
 *      ##### `<ns-add-to-bag>` has no slots for content.
 *  </script>
 * </marked-element>
 *
 *
 */
export class NsAddToBag extends PolymerElement {

  static get properties() {
    return {
      /**
       * This is the string displayed to the user when an item is added to the cart/shopping bag
       */
      translations: {
        type: Object,
        value: {
          productAdded: 'Item added to Bag',
          bundleAdded: 'Bundle added to Bag',
          checkout: 'Checkout',
          continueShopping: 'Continue Shopping'
        }
      },
      /**
       * An item has been added to the cart
       */
      addedToCart: {
        type: Boolean,
        value: false
      },
      /**
      * A flag to display the message for adding a bundle, instead of a single item.
      */
      isBundleMessage: {
        type: Boolean,
        value: false
      },
      /**
       * A flag to allow the "continue to checkout" option and display the corresponding button.
       * If false (which is the default) the message modal will instead auto-close after a set period of time
       */
      checkoutContinue: {
        type: Boolean,
        value: false
      },
      isDemoMode: {
        type: Boolean,
        value: false
      }
    };
  }

  static get template() {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  constructor() {
    super()
  }

  ready() {
    super.ready();
    var that = this;
    /**
     * The publishing of the events.shop.ADD_TO_CART event fires the display of the popup
     */
    events.subscribe(events.shop.SHOW_ADD_TO_BAG, function(option) {
      if (util.getCurrentPageName() === "personal-offer" || util.getCurrentPageName() === "mysite-home") {
          if (option && option === "bundle") {
              that.isBundleMessage = true;
          } else if (option && option === "checkout-continue") {
              that.isBundleMessage = false;
              that.checkoutContinue = true;
          } else {
              that.isBundleMessage = false;
          }
          let modal = that.$.addedToCart;
          modal.style.display = "block";

          if (!that.checkoutContinue) {
              setTimeout(function() {
                  modal.style.display = "none";
              }, 2000);
          }
      }
    });

    this._setCommonStrings();
  }

  /**
   * Function to close the modal
   */
  closeModal() {
    this.$.addedToCart.style.setProperty('display', 'none');
  }

  /**
   * Redirects the user to the cart page
   */
  goToCart() {
    CartService.goToCartPage({isMySite: !!StoreFrontSponsorStorageService.getStoreFrontSponsor()});
  }

  /**
   * Sets translations for to common strings used by the component
   * @private
   */
  _setCommonStrings() {
    let inheritedStrings = [
      {key: "addedToCart", dflt: this.translations.productAdded},
      {key: "bundleAdded", dflt: this.translations.bundleAdded},
      {key: "checkout", dflt: this.translations.checkout},
      {key: "continueShopping", dflt: this.translations.continueShopping}
    ];

    StringService.getStrings(inheritedStrings).then(strings => {
        this._updateComponentStrings(strings);
    });
  }

  /**
   * Sets translations for to common strings used by the component
   * @private
   */
  _updateComponentStrings(strings) {
    this.set("translations.productAdded", strings.addedToCart);
    this.set("translations.bundleAdded", strings.bundleAdded);
    this.set("translations.checkout", strings.checkout);
    this.set("translations.continueShopping", strings.continueShopping);
  }

    /**
     * This function is used exclusively for the demo page.
     */
  demoPublish() {
      events.publish(events.shop.SHOW_ADD_TO_BAG, "checkout-continue")
  }

}
customElements.define('ns-add-to-bag', NsAddToBag);
